import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Smartphone, Bot, Zap, Star, Search, Users, Activity, 
  Calendar, CreditCard, CheckCircle2, ArrowRight, ShieldCheck, 
  Terminal, Globe, Lock, Share2
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState('quarterly');

  const pricingPlans = [
    {
      name: 'Starter',
      desc: 'Perfect for single-location local businesses.',
      quarterly: '₹1,499',
      yearly: '₹4,999',
      features: ['Basic AI Bot', 'Google Maps Auto-Post', 'Review Fetching', 'Manual Lead Blast']
    },
    {
      name: 'Growth',
      desc: 'For aggressive growth and competitor outranking.',
      quarterly: '₹3,999',
      yearly: '₹12,999',
      popular: true,
      features: ['Everything in Starter', 'Facebook & Insta Sync', 'AI Draft Replies', 'Competitor Spy Radar', 'SEO Profile Fixer']
    },
    {
      name: 'Full Suite',
      desc: 'God-mode automation for dominant market leaders.',
      quarterly: '₹7,499',
      yearly: '₹24,999',
      features: ['Everything in Growth', 'Fully Automated Reviews', 'Counter-Attack Blasts', 'All 16 Cron Automations', 'API Endpoints Access']
    }
  ];

  const automations = [
    { name: 'Review Checker', desc: 'Every 15 min' },
    { name: 'Abandoned Lead', desc: 'Every 15 min' },
    { name: 'Daily Follow-up', desc: '10:00 AM' },
    { name: 'Festival Nudge', desc: '09:30 AM' },
    { name: 'ROI Briefing', desc: '07:00 PM' },
    { name: 'Health Score', desc: 'Sunday 2 AM' },
  ];

  // 🔥 DIRECT WHATSAPP REDIRECT FUNCTION 🔥
  const handleWhatsAppRedirect = (planName) => {
    // 👇 YAHAN APNA WHATSAPP NUMBER DAALIYE (Country code 91 ke sath, bina + lagaye) 👇
    const phoneNumber = "919720931177"; 
    
    // Message jo user ke WhatsApp par automatically type ho jayega
    const message = `Hi team! 👋 I was checking out the Pivot-Rank SaaS and I'm interested in the *${planName} Plan*. Can we discuss how it fits my business?`;
    
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen font-sans bg-[#050B14] text-slate-200 selection:bg-teal-500 selection:text-white">
      
      {/* 🌟 PREMIUM NAVBAR */}
      <header className="sticky top-0 z-50 bg-[#050B14]/80 backdrop-blur-xl border-b border-teal-900/30 py-4 px-6 md:px-12 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-teal-500 to-indigo-600 p-2 rounded-xl shadow-[0_0_15px_rgba(20,184,166,0.3)]">
            <Terminal size={22} className="text-white" />
          </div>
          <div className="text-2xl font-black tracking-widest uppercase text-white">
            Pivot<span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-400">-Rank</span>
          </div>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="bg-slate-800 hover:bg-teal-900/50 text-teal-400 border border-teal-800 px-6 py-2 rounded-xl font-bold text-sm transition-all flex items-center gap-2"
        >
          <Lock size={16} /> Secure Login
        </button>
      </header>

      {/* 🚀 HERO SECTION */}
      <section className="relative text-center pt-24 pb-20 px-5 max-w-6xl mx-auto">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-teal-600/10 rounded-full blur-[150px] pointer-events-none"></div>

        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-teal-950/50 border border-teal-500/30 text-teal-400 px-5 py-2 rounded-full text-xs font-black tracking-[0.2em] uppercase mb-8 shadow-inner">
            <Zap size={14} /> 16 Background Automations • 29 APIs
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight">
            Run Your Entire Business From <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-500">WhatsApp</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed font-medium">
            Pivot-Rank is an enterprise-grade AI engine. Command Meta publishing, SEO tracking, review generation, and competitor spying without ever leaving your WhatsApp chat.
          </p>
          <div className="flex justify-center gap-5">
            <button 
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-teal-500 to-indigo-600 hover:scale-105 text-white px-10 py-4 rounded-xl font-black tracking-wide shadow-[0_0_30px_rgba(20,184,166,0.3)] transition-all flex items-center gap-2"
            >
              View Pricing & Plans <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* 📱 THE WHATSAPP COMMAND CENTER */}
      <section className="py-24 px-6 border-y border-teal-900/30 bg-[#08101C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">The Owner's Command Center</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Trigger advanced SaaS functionalities natively through WhatsApp. No clunky dashboards required for daily operations.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Content Card */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-2xl">
              <Share2 className="text-pink-400 mb-4" size={32} />
              <h3 className="text-xl font-bold text-white mb-4">Content & Posting</h3>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> AI Google Maps & Meta Post</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Direct Gallery Uploads</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Offer/Event Publisher</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> AI Tone Selector</li>
              </ul>
            </div>

            {/* SEO Card */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-2xl">
              <Search className="text-amber-400 mb-4" size={32} />
              <h3 className="text-xl font-bold text-white mb-4">SEO & Profile</h3>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Profile Fixer Engine</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Real Keyword Targeting</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Weekly GBP Health Score</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Historic Performance Data</li>
              </ul>
            </div>

            {/* Leads Card */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-2xl">
              <Users className="text-blue-400 mb-4" size={32} />
              <h3 className="text-xl font-bold text-white mb-4">Leads & Marketing</h3>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Multi-Segment WA Blasts</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Competitor Spy Campaign</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Counter-Attack Blasts</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> AI Sales Predictor</li>
              </ul>
            </div>

            {/* Reviews Card */}
            <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-2xl">
              <Star className="text-yellow-400 mb-4" size={32} />
              <h3 className="text-xl font-bold text-white mb-4">Review Management</h3>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Fetch Google Reviews</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Auto-Draft AI Replies</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> 1-Tap Approve & Publish</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-teal-500 shrink-0"/> Fully Automated Mode</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 🤖 THE AI BOT & AUTOMATION ENGINE */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-indigo-900/30 text-indigo-400 px-4 py-1.5 rounded-full text-xs font-bold mb-6">
              <Bot size={14} /> Customer-Facing AI Bot
            </div>
            <h2 className="text-4xl font-black text-white mb-6">Smart AI Sales Agent</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              While you sleep, Pivot-Rank's AI qualifies leads, detects course interests, handles demo booking flows, and seamlessly escalates SOS queries to human coordinators. 
              <strong> Each conversation has independent memory.</strong>
            </p>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <h4 className="text-white font-bold mb-4 flex items-center gap-2"><Zap className="text-yellow-500" size={18}/> 16 Background Cron Jobs</h4>
              <div className="grid grid-cols-2 gap-4">
                {automations.map((job, i) => (
                  <div key={i} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700/50">
                    <p className="text-white text-sm font-bold">{job.name}</p>
                    <p className="text-teal-400 text-xs mt-1">{job.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-xs mt-4 text-center italic">+ 10 more scheduled engine tasks</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-teal-900/40 to-indigo-900/40 border border-teal-800/50 rounded-3xl p-8 relative overflow-hidden shadow-2xl">
             <div className="absolute top-0 right-0 p-4 opacity-10"><Globe size={200} /></div>
             <h3 className="text-2xl font-bold text-white mb-2">Developer Grade Architecture</h3>
             <p className="text-teal-200/70 mb-8">29 Secure HTTP API Endpoints powering the ecosystem.</p>
             
             <div className="space-y-4">
               {['Google & Meta OAuth Integration', 'Razorpay Webhooks & Billing', 'WordPress Publishing Bridge', 'SEO Content Generation Pipeline', 'Client Auto-Registration Endpoints'].map((api, i) => (
                 <div key={i} className="flex items-center gap-3 bg-slate-950/50 p-3 rounded-xl border border-slate-800">
                   <div className="bg-teal-500/20 p-1.5 rounded-md"><CheckCircle2 size={16} className="text-teal-400" /></div>
                   <span className="text-slate-300 text-sm font-medium">{api}</span>
                 </div>
               ))}
             </div>
          </div>
        </div>
      </section>

      {/* 💳 PRICING PLANS */}
      <section id="pricing" className="py-24 px-6 bg-[#08101C] border-t border-teal-900/30">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-black text-white mb-6">Transparent SaaS Pricing</h2>
          
          {/* Billing Toggle */}
          <div className="inline-flex bg-slate-900 border border-slate-800 rounded-xl p-1 mb-12">
            <button 
              onClick={() => setBilling('quarterly')}
              className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all ${billing === 'quarterly' ? 'bg-teal-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
            >
              Quarterly Plans
            </button>
            <button 
              onClick={() => setBilling('yearly')}
              className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${billing === 'yearly' ? 'bg-teal-600 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
            >
              Yearly Plans <span className="bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest">Save Big</span>
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-left">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`bg-slate-900 rounded-3xl p-8 border ${plan.popular ? 'border-teal-500 shadow-[0_0_30px_rgba(20,184,166,0.15)] relative transform md:-translate-y-4' : 'border-slate-800'}`}>
                {plan.popular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-teal-500 text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Most Popular</div>}
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm mb-6 h-10">{plan.desc}</p>
                <div className="mb-6">
                  <span className="text-4xl font-black text-white">{billing === 'quarterly' ? plan.quarterly : plan.yearly}</span>
                  <span className="text-slate-500 text-sm"> / {billing === 'quarterly' ? 'quarter' : 'year'}</span>
                </div>
                
                {/* 🔥 ACTION BUTTON 🔥 */}
                <button 
                  onClick={() => handleWhatsAppRedirect(plan.name)}
                  className={`w-full py-3 rounded-xl font-bold mb-8 transition-all hover:scale-105 shadow-lg ${plan.popular ? 'bg-teal-500 hover:bg-teal-400 text-slate-950 shadow-teal-500/20' : 'bg-slate-800 hover:bg-slate-700 text-white shadow-slate-900/50'}`}
                >
                  Get Started
                </button>

                <div className="space-y-4">
                  {plan.features.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 size={18} className="text-teal-500 shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-sm">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 📜 FOOTER */}
      <footer className="bg-[#03060A] text-slate-500 text-center py-12 text-sm border-t border-slate-900">
<p className="text-slate-400 max-w-2xl mx-auto mb-3 leading-relaxed">
  Pivot-Rank is an AI-powered business growth and local SEO
  platform developed and operated by Pivot Edu Unit, Dehradun.
</p>        <p className="mb-6 text-slate-600">&copy; 2026 Pivot Edu Unit. All rights reserved.</p>
        <div className="flex justify-center gap-8 font-medium">
          <a href="/privacy-policy.html" className="hover:text-teal-400 transition-colors">Privacy Policy</a>
          <a href="/terms.html" className="hover:text-teal-400 transition-colors">Terms of Service</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;